import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCompanyName } from "@/lib/company";
import { Page, PageHeader, StatStrip, Segmented, Card, Loading, usd, plural, pct } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

// design_handoff_team_roles/screens/Objectives.dc.html, the list: money per objective for a month (backend:
// objective_money.month_view). Total cost = agreed fees + AI spend; a fee counts in the month it was agreed, and a fee
// waiting for agreement isn't counted until the professional agrees. Crewbench records fees and never pays them.

// "$600", "$841.20": cents only when there are some
export const money = (n) => usd(n, n % 1 ? 2 : 0);
// whole dollars from $100 up, for the header and the strip
const big = (n) => usd(n, Math.abs(n) >= 100 ? 0 : 2);
const monthName = (m, opts = { month: "long" }) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-GB", opts);
const sum = (rows, k) => rows.reduce((a, r) => a + (r[k] || 0), 0);

const STATES = {
  done: ["Done", "text-gr-success"], review: ["In review", "text-gr-warning"],
  "in progress": ["In progress", "text-gr-ink-secondary"], todo: ["To do", "text-muted-foreground"],
};
// what waits for agreement: a new task's whole fee, or a change's difference
const waitingUsd = (r) => r.waiting.reduce((a, w) => a + (w.kind === "new" ? w.fee_usd : w.fee_usd - w.from_usd), 0);
const offeredUsd = (r) => r.waiting.reduce((a, w) => a + (w.kind === "new" ? w.fee_usd : 0), 0);
function waitingNote(r) {
  if (r.waiting.length > 1) return `${r.waiting.length} waiting for agreement`;
  const [w] = r.waiting;
  return w && (w.kind === "change" ? `Change to ${money(w.fee_usd)} waiting` : "Waiting for their agreement");
}

const COLS = "grid grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1.3fr)_repeat(4,minmax(0,0.8fr))] gap-3 px-5";

function ObjectiveRow({ r, back }) {
  const [state, tone] = STATES[r.status] || STATES.todo;
  const people = r.assigned.map((a) => (a.agreed ? a.name : `${a.name} (not agreed yet)`));
  const offered = offeredUsd(r);
  return (
    <Link to={`/founder/objectives/${r.id}`} state={{ back }} data-testid={`money-row-${r.title}`}
      className={cn(COLS, "py-[9px] border-t items-center hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring")}>
      <span className="text-[13px] leading-5 font-semibold">{r.title}</span>
      <span className={cn("text-xs uppercase tracking-[0.09em]", tone)}>{state}</span>
      <span className={people.length ? "text-gr-ink-secondary" : "text-muted-foreground"}>{people.length ? people.join(", ") : "Nobody assigned"}</span>
      <span className="text-end">{r.budget_usd ? money(r.budget_usd) : "—"}</span>
      <span className="text-end">{usd(r.spent_usd)}</span>
      <span className="text-end flex flex-col items-end">
        <span>{r.fees_usd ? money(r.fees_usd) : offered ? money(offered) : "—"}</span>
        {r.waiting.length > 0 && <span className="text-xs text-gr-warning">{waitingNote(r)}</span>}
      </span>
      <span className="text-end font-semibold">{usd(r.total_usd)}</span>
    </Link>
  );
}

function ProjectCard({ name, rows, month, current, back }) {
  const [budget, spent, fees, total] = ["budget_usd", "spent_usd", "fees_usd", "total_usd"].map((k) => sum(rows, k));
  return (
    <section aria-label={name} className="bg-card rounded-[10px] overflow-hidden" data-testid={`money-project-${name}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-5 py-3.5">
        <h2 className="m-0 font-display text-[15px] leading-[22px] tracking-[0.02em] font-semibold">{name}</h2>
        <span className="text-sm text-muted-foreground tabular-nums">
          {big(spent)} of {big(budget)} AI budget · {fees ? `${big(fees)} in agreed fees` : current ? "no fees this month" : `no fees in ${monthName(month)}`}
        </span>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[900px] text-sm tabular-nums">
          <div className={cn(COLS, "py-2 border-t text-xs uppercase tracking-[0.09em] text-muted-foreground")}>
            <span>Objective</span><span>State</span><span>Assigned</span><span className="text-end">Task budget</span>
            <span className="text-end">AI spent</span><span className="text-end">Fees</span><span className="text-end">Total cost</span>
          </div>
          {rows.map((r) => <ObjectiveRow key={r.id} r={r} back={back} />)}
          <div className={cn(COLS, "py-2.5 border-t bg-gr-surface-2 font-semibold")}>
            <span>{name}, {monthName(month)}</span><span /><span />
            <span className="text-end">{money(budget)}</span><span className="text-end">{usd(spent)}</span>
            <span className="text-end">{fees ? money(fees) : "—"}</span><span className="text-end">{usd(total)}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ObjectivesMoney({ viewSwitch, action, dialog }) {
  const company = useCompanyName();
  const [params, setParams] = useSearchParams();
  const month = params.get("month") || "";
  const project = params.get("project") || "all";
  const { data, isLoading } = useQuery({
    queryKey: ["objectives", "money", month], refetchInterval: 30000,
    queryFn: async () => (await api.get("/objectives/money", { params: month ? { month } : {} })).data,
  });
  const setParam = (k, v, empty) => { const p = new URLSearchParams(params); if (v === empty) p.delete(k); else p.set(k, v); setParams(p, { replace: true }); };
  const eyebrow = [company, "Objectives"].filter(Boolean).join(" · ");
  if (isLoading || !data) return <Page><PageHeader eyebrow={eyebrow} title="Objectives" actions={action} />{viewSwitch}<Loading />{dialog}</Page>;

  const m = data.month;
  const current = m === data.months[0];
  const key = (p) => p ?? "none";
  const projects = data.projects.filter((p) => project === "all" || key(p.id) === project);
  const rows = data.objectives.filter((r) => project === "all" || key(r.project_id) === project);
  const [budgets, spent, fees, total] = ["budget_usd", "spent_usd", "fees_usd", "total_usd"].map((k) => sum(rows, k));
  const waiting = rows.reduce((a, r) => a + waitingUsd(r), 0);
  const before = projects.reduce((a, p) => a + (data.previous_totals[p.id ?? ""] || 0), 0);
  const all = data.objectives;
  const short = monthName(m, { month: "short" }).toUpperCase();
  const back = `/founder/objectives?${params.toString()}`;

  return (
    <Page data-testid="owner-objectives-money">
      <PageHeader eyebrow={eyebrow} title={`What ${company || "your company"} is working on.`}
        status={`${all.filter((r) => r.status !== "done").length} open, ${all.filter((r) => r.done_this_month).length} done in ${monthName(m)}. ${big(sum(all, "total_usd"))} total cost ${current ? "this month" : `in ${monthName(m)}`}.`}
        actions={action} />
      <div className="flex flex-wrap items-center gap-3">
        {viewSwitch}
        {data.projects.length > 1 && (
          <Segmented label="Project" value={project} onChange={(v) => setParam("project", v, "all")} testid="money-project"
            options={[["all", "All projects"], ...data.projects.map((p) => [key(p.id), p.name])]} />
        )}
        <Select value={m} onValueChange={(v) => setParam("month", v, data.months[0])}>
          <SelectTrigger aria-label="Month" className="w-auto min-w-[160px]" data-testid="money-month"><SelectValue /></SelectTrigger>
          <SelectContent>{data.months.map((x) => <SelectItem key={x} value={x}>{monthName(x, { month: "long", year: "numeric" })}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">All amounts in US dollars. Total cost = agreed fees + AI spend.</span>
      </div>
      <StatStrip min={160} cellClassName="px-5 py-3.5 gap-0.5" items={[
        { label: `Task budgets · ${short}`, value: big(budgets), big: true, sub: `Across ${plural(rows.filter((r) => r.budget_usd).length, "objective")}` },
        { label: `AI spent · ${short}`, value: big(spent), big: true, sub: budgets ? `${pct(spent, budgets)}% of the budgets` : "No task budgets set" },
        { label: `Agreed fees · ${short}`, value: big(fees), big: true, sub: waiting > 0 ? `${big(waiting)} more waiting for agreement` : "Nothing waiting for agreement" },
        { label: `Total cost · ${short}`, value: big(total), big: true, sub: `${big(before)} in ${monthName(data.previous_month)}` },
      ]} />
      {projects.map((p) => {
        const mine = rows.filter((r) => r.project_id === p.id);
        return mine.length > 0 && <ProjectCard key={key(p.id)} name={p.name} rows={mine} month={m} current={current} back={back} />;
      })}
      {rows.length === 0 && <Card className="px-5 py-6 text-muted-foreground">Nothing was open or cost anything in {monthName(m)}.</Card>}
      <p className="m-0 text-sm text-muted-foreground [text-wrap:pretty]">
        A fee counts in the month it was agreed. A fee waiting for agreement isn't counted until the professional agrees. Crewbench doesn't pay fees; they're records of what you agreed.
      </p>
      {dialog}
    </Page>
  );
}
